import {
  AlertCircle,
  Calculator,
  Calendar,
  Lock,
  Percent,
  PiggyBank,
  Play,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { Button, DataTable, EmptyState, Spinner, StatCard } from '@/components/ui';
import { useBrokenPeriodCalculation, useCapitaliseInterest } from '@/hooks/use-interest-engine';
import { useInterestSummary, useLoan, useRunAccrual } from '@/hooks/use-loans';
import { handleMutationError } from '@/lib/error-handling';
import { toast } from '@/stores/toast-store';
import type { LoanStatus } from '@/types';

function fmt(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function fmtRate(num: number): string {
  return `${num.toFixed(2)}%`;
}

interface Props {
  loanId: string;
}

interface AccrualRow {
  id: string;
  date: string;
  reference: string;
  description: string;
  amount: number;
}

function formatAccrualDescription(description: string): string {
  const legacyDescription = description.match(
    /^(Daily|Manual) interest accrual @ ([\d.]+)% p\.a\.(?: \(default rate\))?$/i,
  );

  if (!legacyDescription) {
    return description.replace(/ @ /g, ' at ').replace(/p\.a\./gi, 'annual interest');
  }

  const [, accrualType, annualRate] = legacyDescription;
  const rateType = /default rate/i.test(description) ? ' default' : '';
  return `${accrualType} interest accrual at ${annualRate}% annual${rateType} interest`;
}

const ACCRUAL_ELIGIBLE_STATUSES: LoanStatus[] = [
  'DISBURSED',
  'CURRENT',
  'DELINQUENT_30',
  'DELINQUENT_60',
  'DELINQUENT_90',
  'DEFAULT',
  'RESTRUCTURED',
];

const STATUS_LABELS: Record<LoanStatus, string> = {
  APPLICATION: 'Application',
  UNDER_REVIEW: 'Under Review',
  APPROVED: 'Approved',
  DISBURSED: 'Disbursed',
  CURRENT: 'Current',
  DELINQUENT_30: 'Delinquent 30',
  DELINQUENT_60: 'Delinquent 60',
  DELINQUENT_90: 'Delinquent 90',
  DEFAULT: 'Default',
  PAID_OFF: 'Paid Off',
  RESTRUCTURED: 'Restructured',
  CANCELLED: 'Cancelled',
};

export function InterestTab({ loanId }: Props) {
  const { data, isLoading, isError } = useInterestSummary(loanId);
  const { data: loan } = useLoan(loanId);
  const runAccrual = useRunAccrual();
  const capitaliseInterest = useCapitaliseInterest();
  const brokenPeriod = useBrokenPeriodCalculation();

  const [showManualForm, setShowManualForm] = useState(false);
  const [manualDate, setManualDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBrokenPeriod, setShowBrokenPeriod] = useState(false);
  const [brokenStart, setBrokenStart] = useState('');
  const [brokenEnd, setBrokenEnd] = useState('');

  const loanStatus = loan?.status;
  const canRunAccrual = loanStatus ? ACCRUAL_ELIGIBLE_STATUSES.includes(loanStatus) : true;
  const ineligibleStatusLabel = loanStatus ? STATUS_LABELS[loanStatus] : 'this status';

  const recentAccruals: AccrualRow[] = (data?.recentAccruals ?? []).map(entry => ({
    ...entry,
    reference: entry.reference ?? 'N/A',
    description: formatAccrualDescription(entry.description ?? ''),
  }));

  const accrualColumns = [
    {
      key: 'date',
      header: 'Date',
      render: (row: AccrualRow) => new Date(row.date).toLocaleDateString('en-AU'),
    },
    {
      key: 'reference',
      header: 'Reference',
      render: (row: AccrualRow) => (
        <span className="font-mono text-xs text-gray-500">{row.reference}</span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right' as const,
      render: (row: AccrualRow) => <span className="font-semibold">{fmt(row.amount)}</span>,
    },
  ];

  const handleAccrualSuccess = (messagePrefix: string, result: any) => {
    const loanResult = result?.results?.[0];
    if (!loanResult) {
      toast.info(`${messagePrefix}: no accrual was posted`);
      return;
    }

    if (loanResult.error) {
      toast.error(`${loanResult.loanNumber}: ${loanResult.error}`);
      return;
    }

    if (!loanResult.transactionId && !loanResult.accrualRecordId) {
      toast.info(`${loanResult.loanNumber}: no accrual was posted for ${loanResult.accrualDate}`);
      return;
    }

    if (loanResult.alreadyAccrued) {
      toast.info(
        `${loanResult.loanNumber}: interest for ${loanResult.accrualDate} was already accrued`,
      );
      return;
    }

    toast.success(
      `${loanResult.loanNumber}: accrued ${fmt(loanResult.dailyInterest)} for ${loanResult.accrualDate}`,
    );
  };

  const handleRunDailyAccrual = () => {
    if (!canRunAccrual) {
      toast.info(`Loan is not eligible for accrual while status is ${ineligibleStatusLabel}`);
      return;
    }

    runAccrual.mutate(
      { loanId },
      {
        onSuccess: result => handleAccrualSuccess('Daily accrual completed', result),
        onError: (err: any) => handleMutationError(err, 'Failed to run daily accrual'),
      },
    );
  };

  const handleRunManualAccrual = () => {
    if (!manualDate) return;
    if (!canRunAccrual) {
      toast.info(`Loan is not eligible for accrual while status is ${ineligibleStatusLabel}`);
      return;
    }

    runAccrual.mutate(
      { loanId, date: manualDate },
      {
        onSuccess: result => {
          handleAccrualSuccess('Manual accrual completed', result);
          setShowManualForm(false);
        },
        onError: (err: any) => handleMutationError(err, 'Failed to run manual accrual'),
      },
    );
  };

  const handleCapitalise = () => {
    if (!canRunAccrual) {
      toast.info(`Loan is not eligible for accrual while status is ${ineligibleStatusLabel}`);
      return;
    }

    capitaliseInterest.mutate(loanId, {
      onSuccess: () => toast.success('Accrued interest capitalised'),
      onError: (err: any) => handleMutationError(err, 'Failed to capitalise accrued interest'),
    });
  };

  const handleCalculateBrokenPeriod = () => {
    if (!brokenStart || !brokenEnd) return;

    brokenPeriod.mutate(
      { loanId, fromDate: brokenStart, toDate: brokenEnd },
      {
        onError: (err: any) => handleMutationError(err, 'Failed to calculate broken period'),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400">
        <Spinner size="lg" className="w-4 h-4 animate-spin mr-2" />
        Loading interest data...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex items-center justify-center h-48 text-red-500">
        <AlertCircle className="w-4 h-4 mr-2" />
        Failed to load interest data
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Effective Rate"
          value={fmtRate(data.effectiveRate)}
          subtitle={
            data.isDefaultRateApplied
              ? 'Default rate currently applied'
              : data.defaultRate
                ? `Default rate available: ${fmtRate(data.defaultRate)}`
                : 'Standard rate in force'
          }
        />
        <StatCard
          label="Daily Accrual"
          value={fmt(data.dailyAccrual)}
          subtitle={`Approx. ${fmt(data.monthlyAccrual)} per month`}
        />
        <StatCard
          label="Accrued Interest"
          value={fmt(data.totalAccruedInterest)}
          subtitle={`Outstanding interest: ${fmt(data.outstandingInterest)}`}
        />
        <StatCard
          label="Total Owed"
          value={fmt(data.totalOwed)}
          subtitle={`Principal outstanding: ${fmt(data.principalOutstanding)}`}
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 px-6 py-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">Interest Actions</h3>
                {!canRunAccrual && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                    Locked
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Post daily or backdated interest, estimate broken-period interest, and capitalise
                outstanding interest.
              </p>
            </div>

            <div
              className={`grid w-full gap-3 sm:grid-cols-2 xl:max-w-[560px] ${
                !canRunAccrual ? 'opacity-75' : ''
              }`}
            >
              <Button
                variant="primary"
                onClick={handleRunDailyAccrual}
                loading={runAccrual.isPending && !showManualForm}
                disabled={!canRunAccrual}
                className="!w-full justify-center"
              >
                <Play className="w-4 h-4 mr-2" />
                Run Daily Accrual
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowManualForm(prev => !prev)}
                disabled={!canRunAccrual}
                className="!w-full justify-center"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Run Manual Accrual
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowBrokenPeriod(prev => !prev)}
                className="!w-full justify-center"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Broken Period
              </Button>
              <Button
                variant="secondary"
                onClick={handleCapitalise}
                loading={capitaliseInterest.isPending}
                disabled={!canRunAccrual}
                className="!w-full justify-center"
              >
                <PiggyBank className="w-4 h-4 mr-2" />
                Capitalise Interest
              </Button>
            </div>
          </div>

          {!canRunAccrual && loanStatus && (
            <div className="mt-4 rounded-lg border border-[#E7D97C] bg-[#FFF6BF] px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-[#8A6A17]">
                  <Lock className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#5F5413]">
                    Accrual unavailable in {ineligibleStatusLabel}
                  </p>
                  <p className="mt-1 text-sm leading-6 text-[#5F5413]">
                    Interest accrual becomes available after the loan moves into an active servicing
                    status such as Disbursed, Current, Delinquent, Default, or Restructured.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-4">
          {showManualForm && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-lg bg-white p-2 border border-blue-100">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-900">Manual Accrual</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Post interest for a specific accrual date and refresh the visible summary
                    immediately.
                  </p>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                    <div className="sm:w-64">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Accrual Date
                      </label>
                      <input
                        type="date"
                        value={manualDate}
                        onChange={e => setManualDate(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        onClick={handleRunManualAccrual}
                        loading={runAccrual.isPending}
                        className="!w-auto"
                      >
                        Post Accrual
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setShowManualForm(false)}
                        disabled={runAccrual.isPending}
                        className="!w-auto"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showBrokenPeriod && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
              <h4 className="text-sm font-semibold text-gray-900">Broken Period Calculator</h4>
              <p className="text-sm text-gray-600 mt-1">
                Estimate interest for a custom date range without posting an accrual.
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={brokenStart}
                    onChange={e => setBrokenStart(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={brokenEnd}
                    onChange={e => setBrokenEnd(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleCalculateBrokenPeriod}
                  loading={brokenPeriod.isPending}
                  disabled={!brokenStart || !brokenEnd}
                  className="!w-auto"
                >
                  Calculate
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowBrokenPeriod(false)}
                  disabled={brokenPeriod.isPending}
                  className="!w-auto"
                >
                  Close
                </Button>
              </div>

              {brokenPeriod.data && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-amber-100 bg-white p-3">
                    <p className="text-xs text-gray-500">Days</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {brokenPeriod.data.daysCount}
                    </p>
                  </div>
                  <div className="rounded-lg border border-amber-100 bg-white p-3">
                    <p className="text-xs text-gray-500">Daily Rate</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {fmtRate(brokenPeriod.data.rate)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-amber-100 bg-white p-3">
                    <p className="text-xs text-gray-500">Estimated Interest</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {fmt(brokenPeriod.data.amount)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex h-9 overflow-hidden rounded-lg bg-gray-100">
            <div
              className="flex items-center justify-center bg-slate-900 text-white text-xs font-semibold"
              style={{
                width: `${Math.max(8, (data.principalOutstanding / Math.max(data.totalOwed, 1)) * 100)}%`,
              }}
            >
              <span className="truncate px-2">Principal</span>
            </div>
            {data.outstandingInterest > 0 && (
              <div
                className="flex items-center justify-center bg-amber-500 text-white text-xs font-semibold"
                style={{
                  width: `${Math.max(8, (data.outstandingInterest / Math.max(data.totalOwed, 1)) * 100)}%`,
                }}
              >
                <span className="truncate px-2">Interest</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-slate-900" />
              Principal: {fmt(data.principalOutstanding)}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-amber-500" />
              Interest: {fmt(data.outstandingInterest)}
            </span>
            <span className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-green-600" />
              Monthly accrual pace: {fmt(data.monthlyAccrual)}
            </span>
            <span className="flex items-center gap-1.5">
              <Percent className="w-3.5 h-3.5 text-indigo-600" />
              Standard rate: {fmtRate(data.standardRate)}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Recent Accrual Entries</h3>
          <p className="text-sm text-gray-500 mt-1">
            Posted accrual transactions that contribute to the visible interest summary.
          </p>
        </div>
        <div className="p-2">
          {recentAccruals.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="No accrual entries yet"
              description="Run the daily or manual accrual to start building interest history for this loan."
            />
          ) : (
            <DataTable columns={accrualColumns} data={recentAccruals} className="rounded-lg" />
          )}
        </div>
      </div>
    </div>
  );
}
